import { useEffect, useState } from 'react'

/**
 * Live countdown to a split-payment price-hold expiry, with the price-hold warning.
 * Shown on the golfer's pay-share page + the lead's split status screen while a
 * split is collecting. Inline EN/FR (these public split pages switch on a `fr`
 * boolean, not a t() dictionary). Returns null once the hold has passed — the page
 * then shows its own "expired" message. The copy never names a fixed "48 hours"
 * (the hold length is env-configurable + the lead can extend it).
 */
export default function SplitCountdown({ expiresAtMs, fr = false, onExpire }) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (!expiresAtMs) return
    const iv = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(iv)
  }, [expiresAtMs])

  const expired = !!expiresAtMs && (expiresAtMs - now) <= 0
  // The instant our 1s clock crosses zero, tell the page so its OWN expired line
  // takes over on the same second (PayShare doesn't poll while unpaid; the lead
  // screen's `now` only ticks per-minute — without this there'd be a gap where the
  // clock vanished but the expired message hadn't appeared yet).
  useEffect(() => { if (expired && onExpire) onExpire() }, [expired]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!expiresAtMs || expired) return null // expired -> the page's own expired line takes over
  const remaining = expiresAtMs - now

  const urgent = remaining <= 3 * 3600 * 1000
  const totalSec = Math.floor(remaining / 1000)
  const d = Math.floor(totalSec / 86400)
  const h = Math.floor((totalSec % 86400) / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  const parts = []
  if (d) parts.push(`${d}${fr ? 'j' : 'd'}`)
  if (d || h) parts.push(`${h}h`)
  parts.push(`${String(m).padStart(2, '0')}m`)
  if (!d) parts.push(`${String(s).padStart(2, '0')}s`) // drop seconds once we're a day+ out
  const label = parts.join(' ')

  const fg = urgent ? '#b91c1c' : '#0f172a'
  const bg = urgent ? '#fef2f2' : '#f8fafc'
  const bd = urgent ? '#fecaca' : '#e2e8f0'

  return (
    <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: bg, border: `1px solid ${bd}`, lineHeight: 1.5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 12.5, color: '#475569', fontWeight: 600 }}>{fr ? 'Prix garanti — expire dans' : 'Price locked — expires in'}</span>
        <b style={{ fontSize: 16, color: fg, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{label}</b>
      </div>
      <div style={{ marginTop: 4, fontSize: 11.5, color: urgent ? '#9a3412' : '#94a3b8' }}>
        {fr
          ? "Votre prix est garanti jusqu'à la fin de ce compte à rebours. Passé ce délai, les prix de l'hôtel et des départs peuvent changer."
          : 'Your price is locked until this countdown reaches zero. After it expires, the hotel and tee-time prices may change.'}
      </div>
    </div>
  )
}
